import { lazy, Suspense, type FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { Spin } from 'antd';
import { MainLayout } from '@/layouts/MainLayout';
import {
  trackPageView,
  trackSource,
  trackFunnelStep,
  recordPageInJourney,
  sendNavigationJourney,
  trackReturnVisitor,
} from '@/utils/analytics';
import { useExitIntent } from '@/hooks/useExitIntent';

// Home is eagerly loaded — it's the landing page, must render instantly for FCP/LCP
import { Home } from '@/pages/Home';

// Lazy load all other pages for code splitting
const About = lazy(() =>
  import('@/pages/About').then((m) => ({ default: m.About })),
);
const ExperiencePage = lazy(() =>
  import('@/pages/Experience').then((m) => ({ default: m.ExperiencePage })),
);
const BlogList = lazy(() =>
  import('@/pages/Blog/BlogList').then((m) => ({ default: m.BlogList })),
);
const BlogArticle = lazy(() =>
  import('@/pages/Blog/BlogArticle').then((m) => ({
    default: m.BlogArticle,
  })),
);
const ContactPage = lazy(() =>
  import('@/pages/Contact').then((m) => ({ default: m.ContactPage })),
);
const NotFound = lazy(() =>
  import('@/pages/NotFound').then((m) => ({ default: m.NotFound })),
);
const StorePage = lazy(() =>
  import('@/pages/Store/StorePage').then((m) => ({ default: m.StorePage })),
);
const ProductDetail = lazy(() =>
  import('@/pages/Store/ProductDetail').then((m) => ({ default: m.ProductDetail })),
);
const CheckoutSuccess = lazy(() =>
  import('@/pages/Store/CheckoutSuccess').then((m) => ({ default: m.CheckoutSuccess })),
);
const OrderLookup = lazy(() =>
  import('@/pages/Store/OrderLookup').then((m) => ({ default: m.OrderLookup })),
);

// Lazy load admin pages (separate chunk)
const AdminLoginPage = lazy(() =>
  import('@/admin/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const AdminDashboard = lazy(() =>
  import('@/admin/pages/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const AdminBlogEditor = lazy(() =>
  import('@/admin/pages/BlogEditor').then((m) => ({ default: m.BlogEditor })),
);
const AdminExperienceEditor = lazy(() =>
  import('@/admin/pages/ExperienceEditor').then((m) => ({ default: m.ExperienceEditor })),
);
const AdminProjectsEditor = lazy(() =>
  import('@/admin/pages/ProjectsEditor').then((m) => ({ default: m.ProjectsEditor })),
);
const AdminTestimonialsEditor = lazy(() =>
  import('@/admin/pages/TestimonialsEditor').then((m) => ({ default: m.TestimonialsEditor })),
);
const AdminSiteContentEditor = lazy(() =>
  import('@/admin/pages/SiteContentEditor').then((m) => ({ default: m.SiteContentEditor })),
);
const AdminResumeManager = lazy(() =>
  import('@/admin/pages/ResumeManager').then((m) => ({ default: m.ResumeManager })),
);
const AdminSubscribersManager = lazy(() =>
  import('@/admin/pages/SubscribersManager').then((m) => ({ default: m.SubscribersManager })),
);
const AdminProductsEditor = lazy(() =>
  import('@/admin/pages/ProductsEditor').then((m) => ({ default: m.ProductsEditor })),
);
const AdminOrdersManager = lazy(() =>
  import('@/admin/pages/OrdersManager').then((m) => ({ default: m.OrdersManager })),
);
const AdminBookingsManager = lazy(() =>
  import('@/admin/pages/BookingsManager').then((m) => ({ default: m.BookingsManager })),
);
const AdminLayout = lazy(() =>
  import('@/admin/components/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const AdminRoute = lazy(() =>
  import('@/admin/components/AdminRoute').then((m) => ({ default: m.AdminRoute })),
);
const AuthProvider = lazy(() =>
  import('@/admin/context/AuthContext').then((m) => ({ default: m.AuthProvider })),
);

/**
 * Persistent spinner wrapper.
 * The Spin component is always mounted (never unmounted/remounted),
 * so its CSS animation stays buttery smooth across Suspense transitions.
 * When the lazy child resolves, the spinner hides via CSS — not unmount.
 */
const SuspenseWrapper: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);

  return (
    <>
      {/* Spinner layer — always mounted, hidden once content arrives */}
      <div
        style={{
          display: ready ? 'none' : 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 128px)',
        }}
      >
        <Spin size="large" />
      </div>
      {/* Content layer */}
      <Suspense
        fallback={
          /* When suspending, keep spinner visible (ready stays false) */
          <SetReady setReady={setReady} value={false} />
        }
      >
        <SetReady setReady={setReady} value />
        {children}
      </Suspense>
    </>
  );
};

/** Tiny effect component: sets the ready flag when it mounts */
const SetReady: FC<{
  setReady: (v: boolean) => void;
  value: boolean;
}> = ({ setReady, value }) => {
  useEffect(() => {
    setReady(value);
  }, [setReady, value]);
  return null;
};

// Analytics wrapper to track page views on navigation
const AnalyticsWrapper: FC<{
  isDark: boolean;
  onToggleTheme: () => void;
}> = (props) => {
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);

  // Global exit intent tracking
  useExitIntent(location.pathname);

  // Return visitor detection (once per session)
  useEffect(() => {
    trackReturnVisitor();
  }, []);

  // Send navigation journey on tab close / unload
  useEffect(() => {
    window.addEventListener('beforeunload', sendNavigationJourney);
    return () => window.removeEventListener('beforeunload', sendNavigationJourney);
  }, []);

  // Enable CSS entrance animations only on client-side navigation.
  // On hard load / refresh the attribute is absent so content appears instantly.
  useEffect(() => {
    if (prevPathRef.current !== null && prevPathRef.current !== location.pathname) {
      document.documentElement.setAttribute('data-navigated', '');
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    // Default behavior: scroll to top on every route change.
    // Skip if a page has flagged an upcoming scroll restore — that page's
    // useEffect runs FIRST (children fire before parents) but parent effects
    // would otherwise overwrite the restored position.
    //
    // The flag is only "live" for its matching destination — otherwise a
    // stale flag from a previous projects-card click would suppress
    // scroll-to-top when the user navigates from project-detail to /blog
    // (or any other unrelated page).
    const pathname = location.pathname;
    const projectsRestoreLive =
      sessionStorage.getItem('projects-restore-scroll') !== null &&
      (pathname === '/portfolio' || pathname === '/');
    const blogRestoreLive =
      sessionStorage.getItem('blog-restore-scroll') !== null &&
      pathname === '/blog';
    const servicesRestoreLive =
      sessionStorage.getItem('services-restore-scroll') !== null &&
      pathname === '/services';
    const hasPendingRestore =
      projectsRestoreLive || blogRestoreLive || servicesRestoreLive;
    if (!hasPendingRestore) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    trackPageView(location.pathname);
    trackSource();
    recordPageInJourney(location.pathname);

    // Recruiter funnel: track key page visits as funnel steps
    const funnelPages: Record<string, string> = {
      '/': 'landing',
      '/about-me': 'about_view',
      '/career': 'experience_view',
      '/contact': 'contact_view',
      '/portfolio': 'projects_view',
    };
    const step = funnelPages[location.pathname];
    if (step) {
      trackFunnelStep(step, location.pathname);
    }
  }, [location.pathname]);

  return <MainLayout {...props} />;
};

interface AppRouterProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

export const AppRouter: FC<AppRouterProps> = ({ isDark, onToggleTheme }) => {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: (
            <AnalyticsWrapper isDark={isDark} onToggleTheme={onToggleTheme} />
          ),
          children: [
            {
              index: true,
              element: <Home />,
            },
            {
              path: 'about-me',
              element: (
                <SuspenseWrapper>
                  <About />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'career',
              element: (
                <SuspenseWrapper>
                  <ExperiencePage />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'portfolio',
              // Route-level lazy: navigation awaits the chunk BEFORE finishing,
              // so back-nav from ProjectDetail never falls through to the
              // Suspense spinner. Combined with the preload on ProjectDetail
              // mount, this is instant.
              lazy: async () => {
                const m = await import('@/pages/Projects');
                return { Component: m.ProjectsPage };
              },
            },
            {
              path: 'portfolio/:id',
              // Use React Router's route-level lazy: the navigation awaits
              // the chunk BEFORE completing, so the view-transition snapshots
              // the fully-loaded ProjectDetail (no Suspense fallback flash).
              // Combined with the preload in ProjectCard, this is instant.
              lazy: async () => {
                const m = await import('@/pages/ProjectDetail');
                return { Component: m.ProjectDetail };
              },
            },
            {
              path: 'blog',
              element: (
                <SuspenseWrapper>
                  <BlogList />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'blog/:slug',
              element: (
                <SuspenseWrapper>
                  <BlogArticle />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'services',
              element: (
                <SuspenseWrapper>
                  <StorePage />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'services/success',
              element: (
                <SuspenseWrapper>
                  <CheckoutSuccess />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'services/orders',
              element: (
                <SuspenseWrapper>
                  <OrderLookup />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'services/:slug',
              element: (
                <SuspenseWrapper>
                  <ProductDetail />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'contact',
              element: (
                <SuspenseWrapper>
                  <ContactPage />
                </SuspenseWrapper>
              ),
            },
            {
              path: '*',
              element: (
                <SuspenseWrapper>
                  <NotFound />
                </SuspenseWrapper>
              ),
            },
          ],
        },
        // Admin routes
        {
          path: '/admin/login',
          element: (
            <SuspenseWrapper>
              <AuthProvider>
                <AdminLoginPage />
              </AuthProvider>
            </SuspenseWrapper>
          ),
        },
        {
          path: '/admin',
          element: (
            <SuspenseWrapper>
              <AuthProvider>
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              </AuthProvider>
            </SuspenseWrapper>
          ),
          children: [
            {
              index: true,
              element: (
                <SuspenseWrapper>
                  <AdminDashboard />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'blog',
              element: (
                <SuspenseWrapper>
                  <AdminBlogEditor />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'experience',
              element: (
                <SuspenseWrapper>
                  <AdminExperienceEditor />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'projects',
              element: (
                <SuspenseWrapper>
                  <AdminProjectsEditor />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'testimonials',
              element: (
                <SuspenseWrapper>
                  <AdminTestimonialsEditor />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'site-content',
              element: (
                <SuspenseWrapper>
                  <AdminSiteContentEditor />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'resume',
              element: (
                <SuspenseWrapper>
                  <AdminResumeManager />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'subscribers',
              element: (
                <SuspenseWrapper>
                  <AdminSubscribersManager />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'products',
              element: (
                <SuspenseWrapper>
                  <AdminProductsEditor />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'orders',
              element: (
                <SuspenseWrapper>
                  <AdminOrdersManager />
                </SuspenseWrapper>
              ),
            },
            {
              path: 'bookings',
              element: (
                <SuspenseWrapper>
                  <AdminBookingsManager />
                </SuspenseWrapper>
              ),
            },
          ],
        },
      ]),
    [isDark, onToggleTheme],
  );

  return <RouterProvider router={router} />;
};
